import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import {
  type IAbility,
  useDeleteSchoolAbilityMutation,
  useGetSchoolAbilitiesQuery,
  useReorderSchoolAbilitiesMutation,
} from '@/api/endpoints/schools/schoolSubjectionParams/abilities';
import { DeleteConfirmationModal, DraggableTable, FetchDataWrapper } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateAbilityModal } from './components/Modals/CreateAbilityModal';
import { EditAbilityModal } from './components/Modals/EditAbilityModal';
import { getColumnsData } from './helpers';
import styles from './SchoolAbilitiesSubsection.module.scss';
import type { IAbilityItemData } from './types';

type TProps = {
  id: number;
  name: string;
};

export const SchoolAbilitiesSubsection = ({ id, name }: TProps) => {
  const navigate = useNavigate();

  const {
    data: abilitiesData,
    isLoading: areAbilitiesLoading,
    isFetching: areAbilitiesFetching,
    isError: isAbilitiesFetchError,
  } = useGetSchoolAbilitiesQuery({ schoolId: id });
  const [reorderAbilities] = useReorderSchoolAbilitiesMutation();
  const [deleteAbility, { isLoading: isAbilityDeleting }] = useDeleteSchoolAbilityMutation();

  const [tableData, setTableData] = useState<IAbility[]>([]);

  const [isCreateAbilityModalOpened, setIsCreateAbilityModalOpened] = useState(false);
  const [isEditAbilityModalOpened, setIsEditAbilityModalOpened] = useState(false);
  const [isDeleteAbilityModalOpened, setIsDeleteAbilityModalOpened] = useState(false);

  const [currentAbilityItem, setCurrentAbilityItem] = useState<null | IAbilityItemData>(null);

  const openCreateAbilityModal = useCallback(() => setIsCreateAbilityModalOpened(true), []);
  const closeCreateAbilityModal = useCallback(() => setIsCreateAbilityModalOpened(false), []);

  const openEditAbilityModal = useCallback(() => setIsEditAbilityModalOpened(true), []);
  const closeEditAbilityModal = useCallback(() => setIsEditAbilityModalOpened(false), []);

  const openDeleteAbilityModal = useCallback(() => setIsDeleteAbilityModalOpened(true), []);
  const closeDeleteAbilityModal = useCallback(() => setIsDeleteAbilityModalOpened(false), []);

  const sendApiRequest = useApiRequest();

  const setItemToOperate = useCallback((data: IAbilityItemData | null) => setCurrentAbilityItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const deleteAbilityHandle = useCallback(() => {
    if (currentAbilityItem) {
      sendApiRequest({
        successMessage: 'Навык успешно удалён',
        apiRequestCallback: () =>
          deleteAbility({
            schoolId: id,
            abilityId: currentAbilityItem?.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteAbilityModal),
      });
    }
  }, [currentAbilityItem?.id, id]);

  const columns = useMemo(
    () => getColumnsData({ openEditAbilityModal, openDeleteAbilityModal, setItemToOperate }),
    [openEditAbilityModal, openDeleteAbilityModal],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      const reorderedArrayIds = reorderedArray.map((ability) => ability.id);

      sendApiRequest({
        successMessage: 'Порядок навыков успешно изменён',
        apiRequestCallback: () => reorderAbilities({ schoolId: id, data: { order: reorderedArrayIds } }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  useEffect(() => {
    setTableData(abilitiesData ?? []);
  }, [abilitiesData]);

  return (
    <FetchDataWrapper
      isLoading={areAbilitiesLoading}
      isError={isAbilitiesFetchError}
    >
      {abilitiesData && (
        <div className={styles.wrapper}>
          <Breadcrumb
            items={[
              { title: 'Публичные страницы' },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`}>Школы</Link> },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`}>{name}</Link> },
              { title: 'Чему вы научитесь' },
            ]}
          />
          <div className={styles['add-button-wrapper']}>
            <Button
              type="primary"
              onClick={openCreateAbilityModal}
              className={styles.button}
            >
              Добавить навык
            </Button>
          </div>
          <DraggableTable
            onDragEnd={onDragEnd}
            keysForSorting={tableData.map((i) => i.position)}
            columns={columns}
            rowKey="position"
            size="large"
            locale={TABLE_TRANSLATIONS}
            pagination={false}
            dataSource={tableData}
            loading={areAbilitiesFetching}
          />
          <div className={styles['action-buttons-wrapper']}>
            <Button
              onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`)}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
          </div>
          {isDeleteAbilityModalOpened && (
            <DeleteConfirmationModal
              isModalOpen={isDeleteAbilityModalOpened}
              description="Вы уверены, что хотите удалить навык?"
              title="Удаление навыка"
              handleCancel={() => handleWithClearOperateItem(closeDeleteAbilityModal)}
              handleOk={deleteAbilityHandle}
              itemToOperate={currentAbilityItem}
              shouldDisableElement={isAbilityDeleting}
            />
          )}
          {isCreateAbilityModalOpened && (
            <CreateAbilityModal
              isModalOpen={isCreateAbilityModalOpened}
              handleCancel={closeCreateAbilityModal}
              schoolId={id}
            />
          )}
          {isEditAbilityModalOpened && currentAbilityItem && (
            <EditAbilityModal
              isModalOpen={isEditAbilityModalOpened}
              handleCancel={() => handleWithClearOperateItem(closeEditAbilityModal)}
              handleClearOperateItem={() => handleWithClearOperateItem()}
              itemToOperate={currentAbilityItem}
              schoolId={id}
            />
          )}
        </div>
      )}
    </FetchDataWrapper>
  );
};
